import React, { useEffect, useState } from 'react'
import { ScrollView } from 'react-native'

import Header from '@zet/common/components/header'
import { useToast } from '@zet/common/components/toast'
import { useDownloadFile } from '@zet/common/hooks/useDownloadFile'
import { colors } from '@zet/design-system'
import { Accordian } from '@zet/design-system/accordian'
import { Button } from '@zet/design-system/button'
import { Icon } from '@zet/design-system/icons'
import { Input } from '@zet/design-system/input'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import Download from '@zet/design-system/svg/Download'
import { B1, B2, H5, Text } from '@zet/design-system/typography'
import { i18n } from '@zet/localization'
import _ from 'lodash'

import { useTdsReports } from '../../api/earnings/hooks'
import { TdsReport } from '../../api/earnings/types'
import { availableFinancialYears } from './constants'
import SelectOptions from './selectTdsOptions'

const TDS = () => {
  const [selectedQuarter, setSelectedQuarter] = useState<string>('')
  const [isQuarterOptionsVisible, setIsQuarterOptionsVisible] =
    useState<boolean>(false)
  const [selectedFinancialYear, setSelectedFinancialYear] = useState<string>(
    availableFinancialYears[availableFinancialYears.length - 1]
  )
  const [isFinancialYearOptionsVisible, setIsFinancialYearOptionsVisible] =
    useState<boolean>(false)

  const { data, isLoading } = useTdsReports(selectedFinancialYear)
  const noDataString = i18n.t('tds.noData').trim().slice(0, 35) + '...'

  const toast = useToast()

  const onSuccess = () => {
    toast?.show({
      intent: 'success',
      heading: i18n.t('toastMessages.success'),
      description: i18n.t('toastMessages.reportDownloaded'),
      hideAfter: 3000,
    })
    setIsDownloading(false)
  }
  const onError = (isToastShown = false) => {
    !isToastShown &&
      toast?.show({
        intent: 'error',
        heading: i18n.t('toastMessages.error'),
        description: i18n.t('toastMessages.tryAfterSometime'),
        hideAfter: 3000,
      })
    setIsDownloading(false)
  }
  const [isDownloading, setIsDownloading] = useState(false)
  const { downloadFile } = useDownloadFile({
    onSuccess,
    onProgress: () => setIsDownloading(true),
    onError,
  })

  const onDownload = (selectedQuarter: string, reports: TdsReport[]) => {
    const selectedReport = _.find(
      reports,
      (r: TdsReport) => r.label === selectedQuarter
    )
    downloadFile(selectedReport?.fileLink)
    // downloadFile('https://www.clickdimensions.com/links/TestPDFfile.pdf')
  }

  useEffect(() => {
    if (data?.reports && data?.reports?.length > 0) {
      setSelectedQuarter(data?.reports[0]?.label)
    } else {
      setSelectedQuarter(noDataString)
    }
  }, [data])

  return (
    <Stack className="flex-1 bg-white">
      <Header showBackIcon title="TDS" />
      <ScrollView showsVerticalScrollIndicator={false}>
        <Stack className="p-4">
          <B2 className="mb-3">{i18n.t('tds.title')}</B2>
          <B1>{i18n.t('tds.financialYear')}</B1>
          <Pressable
            onPressIn={() => {
              setIsFinancialYearOptionsVisible(true)
            }}
            eventName="click_action"
            eventPayload={{ item_name: 'earnings_TDS_certificate_year_filter' }}
            className="mb-2"
          >
            <Input
              isEditable={false}
              placeholder={i18n.t('tds.selectYear')}
              iconRight={
                <Icon
                  type="ArrowDown"
                  color={colors.grays[80]}
                  className="mr-2 mt-1"
                />
              }
              iconLeft={
                <Icon
                  type="DayOutlined"
                  color={colors.grays[50]}
                  className="mr-2 border"
                />
              }
              className="mt-[2] justify-center"
              style={{
                color: colors.grays[100],
                fontFamily: 'Graphie-Bold',
              }}
              value={selectedFinancialYear}
            />
          </Pressable>
          <B1>{i18n.t('tds.selectQuarter')}</B1>
          <Pressable
            onPressIn={() => {
              if (data?.reports && data?.reports?.length > 0) {
                setIsQuarterOptionsVisible(true)
              }
            }}
            className="mb-2"
          >
            <Input
              isEditable={false}
              placeholder={i18n.t('tds.select')}
              iconRight={
                <Icon
                  type="ArrowDown"
                  color={colors.grays[80]}
                  className="mr-2 mt-1"
                />
              }
              iconLeft={
                <Icon
                  type="DayOutlined"
                  color={colors.grays[50]}
                  className="mr-3 border"
                />
              }
              className="mt-[2] justify-center"
              style={{
                color:
                  noDataString === selectedQuarter
                    ? colors.grays[50]
                    : colors.grays[100],
                fontFamily: 'Graphie-Bold',
              }}
              value={selectedQuarter}
            />
          </Pressable>
          <Button
            fullWidth
            size="lg"
            onPress={() => onDownload(selectedQuarter, data?.reports)}
            intent="primary"
            disabled={noDataString === selectedQuarter}
            loading={isDownloading}
            eventName="click_action"
            eventPayload={{ item_name: 'earnings_TDS_certificate_download' }}
          >
            <XStack className="items-center space-x-2">
              <Download color="white" />
              <Text className="text-neutral-white100">
                {i18n.t('buttonGroup.download')}
              </Text>
            </XStack>
          </Button>
          <Stack className="mt-6 bg-white">
            <H5 className="pb-3 text-center">{i18n.t('faqs')}</H5>

            {i18n.t('tds.faqs_content').map((e) => (
              <Accordian
                title={e.question}
                content={e.answer}
                style={{
                  marginTop: 0,
                }}
              />
            ))}
          </Stack>
        </Stack>
        <Stack className="mt-4">
          <SelectOptions
            options={availableFinancialYears}
            setIsBottomSheetVisible={setIsFinancialYearOptionsVisible}
            isBottomSheetVisible={isFinancialYearOptionsVisible}
            setSelectedValue={setSelectedFinancialYear}
            selectedValue={selectedFinancialYear}
            title={`${i18n.t('tds.select')} ${i18n.t('tds.financialYear')}`}
          />
          <SelectOptions
            options={data?.reports}
            setIsBottomSheetVisible={setIsQuarterOptionsVisible}
            isBottomSheetVisible={isQuarterOptionsVisible}
            setSelectedValue={setSelectedQuarter}
            selectedValue={selectedQuarter}
            title={i18n.t('tds.selectQuarter')}
          />
        </Stack>
      </ScrollView>
    </Stack>
  )
}

export default TDS
